import { useMemo, useState } from 'react';
import { routes, type DonestructionRoute, type DirectionStep } from './data/routes';

function cx(...classes: Array<string | false | undefined>) {
  return classes.filter(Boolean).join(' ');
}

function formatDate(date: string) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  }).format(new Date(`${date}T12:00:00`));
}

export default function App() {
  const [selectedId, setSelectedId] = useState('2026-07-03');
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [futureGoal, setFutureGoal] = useState('');
  const [rerouteNote, setRerouteNote] = useState('');

  const selected: DonestructionRoute = routes.find((route) => route.id === selectedId) ?? routes[1];
  const checkedCount = selected.steps.filter((step) => checked[step.id]).length;
  const allChecked = selected.steps.length > 0 && checkedCount === selected.steps.length;
  const progress = selected.steps.length ? Math.round((checkedCount / selected.steps.length) * 100) : 0;

  const nextUnchecked = useMemo(
    () => selected.steps.find((step) => !checked[step.id]) ?? selected.steps[selected.steps.length - 1],
    [checked, selected]
  );

  function toggleStep(step: DirectionStep) {
    setChecked((current) => ({ ...current, [step.id]: !current[step.id] }));
  }

  function setAll(value: boolean) {
    setChecked((current) => {
      const next = { ...current };
      selected.steps.forEach((step) => {
        next[step.id] = value;
      });
      return next;
    });
  }

  return (
    <main className="app-shell">
      <aside className="time-rail" aria-label="Donestructions time navigation">
        <div className="brand-lockup">
          <div className="brand-mark">→</div>
          <div>
            <p className="eyebrow">Donestructions</p>
            <h1>Workday GPS</h1>
          </div>
        </div>

        <div className="rail-section">
          <p className="rail-label">Time Machine</p>
          <nav className="day-list">
            {routes.map((route) => (
              <button
                key={route.id}
                className={cx('day-pill', selected.id === route.id && 'active', route.temporal)}
                onClick={() => setSelectedId(route.id)}
              >
                <span>{route.label}</span>
                <strong>{formatDate(route.date)}</strong>
              </button>
            ))}
          </nav>
        </div>

        <div className="rail-section">
          <p className="rail-label">On this page</p>
          <nav className="toc-list">
            {selected.toc.map((item) => (
              <a href={`#${item.id}`} key={item.id}>{item.title}</a>
            ))}
          </nav>
        </div>
      </aside>

      <section className="route-map">
        <header className="hero-card">
          <div className="hero-topline">
            <span className="status-chip">{selected.status === 'next' ? 'Next turn' : selected.status}</span>
            <span>{selected.owner}</span>
            <span>{selected.timebox}</span>
          </div>
          <h2>{selected.nextTurn}</h2>
          <p>{selected.destination}</p>
          <div className="hero-actions">
            <a className="primary-action" href="#steps">Start directions</a>
            {selected.instructionPath && (
              <a className="secondary-action" href={selected.instructionPath}>Open full instructions</a>
            )}
          </div>
        </header>

        <div className="main-grid">
          <article className="route-content">
            <section id="destination" className="memo-card destination-card">
              <div className="section-kicker">Destination</div>
              <h3>{selected.destination}</h3>
              <p className="coach-line">A good Donestruction does not ask you to re-plan the day. It gives you the next turn and tells you what arrival looks like.</p>
            </section>

            <section id="current-location" className="memo-card">
              <div className="section-kicker">You are here</div>
              <p>{selected.currentLocation}</p>
              {selected.source && <p className="source-line">Source: {selected.source}</p>}
            </section>

            <section id="steps" className="memo-card steps-card">
              <div className="steps-header">
                <div>
                  <div className="section-kicker">Turn-by-turn directions</div>
                  <h3>{checkedCount}/{selected.steps.length} route steps complete</h3>
                </div>
                <button className="check-all" onClick={() => setAll(!allChecked)}>
                  {allChecked ? 'Clear route' : 'Check all'}
                </button>
              </div>

              <div className="progress-track" aria-label={`${progress}% complete`}>
                <div style={{ width: `${progress}%` }} />
              </div>

              <ol className="direction-list">
                {selected.steps.map((step, index) => (
                  <li key={step.id} className={cx('direction-step', checked[step.id] && 'checked')}>
                    <label className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={Boolean(checked[step.id])}
                        onChange={() => toggleStep(step)}
                      />
                      <span className="custom-box" aria-hidden="true">✓</span>
                      <span className="step-number">{String(index + 1).padStart(2, '0')}</span>
                      <span className="step-title">{step.title}</span>
                    </label>
                    <p className="coach-note">{step.coachNote}</p>
                    <div className="how-box">
                      <strong>How to do it</strong>
                      <p>{step.howTo}</p>
                    </div>
                    <div className="done-box">
                      <strong>Arrival signal</strong>
                      <p>{step.doneSignal}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {selected.memos && selected.memos.length > 0 && (
              <section id="memos" className="memo-card memos-card">
                <div className="section-kicker">Memos timeline</div>
                <h3>Running stream inside the day</h3>
                <p className="coach-line">Outline holds the authored route. Memos holds the quick captures, coach notes, evidence snippets, gym prompts, and arrivals that happen while the day is moving.</p>
                <ol className="memos-list">
                  {selected.memos.map((memo) => (
                    <li key={memo.id}>
                      <span className={`memo-type ${memo.type}`}>{memo.type}</span>
                      <time>{memo.time}</time>
                      <p>{memo.content}</p>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            <section id="detour" className="memo-card detour-card">
              <div className="section-kicker">Detour / reroute</div>
              <h3>If reality changes, do this instead</h3>
              <p>{selected.detour}</p>
              <textarea
                value={rerouteNote}
                onChange={(event) => setRerouteNote(event.target.value)}
                placeholder="What changed? e.g. Only 25 minutes before pickup, client pinged, access failed…"
              />
              {rerouteNote && (
                <p className="reroute-output">Reroute draft: shrink the route to the next visible arrival signal, then carry forward only the blocker.</p>
              )}
            </section>

            <section id="done" className="memo-card arrival-card">
              <div className="section-kicker">Arrival criteria</div>
              <h3>{allChecked ? 'You arrived.' : 'You are not done until this is true:'}</h3>
              <p>{selected.definitionOfDone}</p>
            </section>
          </article>

          <aside className="coach-panel" aria-label="Coach guidance panel">
            <div className="coach-card sticky-card">
              <p className="eyebrow">Coach view</p>
              <h3>{allChecked ? 'Route complete' : 'Next best turn'}</h3>
              <p>{allChecked ? selected.definitionOfDone : nextUnchecked?.title}</p>
              {!allChecked && nextUnchecked && <span className="next-mini">Do this before opening a new loop.</span>}
            </div>

            <div className="coach-card">
              <p className="eyebrow">Future day goal</p>
              <textarea
                value={futureGoal}
                onChange={(event) => setFutureGoal(event.target.value)}
                placeholder="For a future day: By the end of this day, we need to have…"
              />
              <p className="tiny-copy">Opening a future day should set a destination, not create a giant task list.</p>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
